import { useCallback, type Dispatch, type SetStateAction } from 'react';
import type { DepartmentValueItem } from '@/components/DepartmentCombo/DepartmentCombo';
import type { EditionValueItem } from '@/components/EditionCombo/EditionCombo';
import type { EditorValueItem } from '@/components/EditorCombo/EditorCombo';

/**
 * Единая логика связи: редактор → редакция и подразделение; редакция → подразделение;
 * сброс редакции → сброс редактора; сброс подразделения → сброс редакции и редактора;
 * выбранное подразделение ограничивает списки редакции и редактора.
 */
export function useEditionEditorDepartmentHandlers(
  setDepartment: Dispatch<SetStateAction<DepartmentValueItem | null>>,
  setEdition: Dispatch<SetStateAction<EditionValueItem | null>>,
  setEditor: Dispatch<SetStateAction<EditorValueItem | null>>
) {
  const onDepartmentChange = useCallback(
    (d: DepartmentValueItem | null) => {
      setDepartment(d);
      if (!d) {
        setEdition(null);
        setEditor(null);
        return;
      }
      setEdition((prev) => {
        if (!prev?.department_id) return prev;
        return prev.department_id === d.id ? prev : null;
      });
      setEditor((prev) => {
        if (!prev?.department_id) return prev;
        return prev.department_id === d.id ? prev : null;
      });
    },
    [setDepartment, setEdition, setEditor]
  );

  const onEditionChange = useCallback(
    (ed: EditionValueItem | null) => {
      setEdition(ed);
      if (ed === null) {
        setEditor(null);
        return;
      }
      if (ed.department_id != null && ed.department_code != null) {
        setDepartment({ id: ed.department_id, code: ed.department_code });
      }
      setEditor((prev) => (prev && prev.edition_id !== ed.id ? null : prev));
    },
    [setDepartment, setEdition, setEditor]
  );

  const onEditorChange = useCallback(
    (ed: EditorValueItem | null) => {
      setEditor(ed);
      if (!ed) return;
      if (ed.edition_id && ed.edition_name) {
        setEdition({
          id: ed.edition_id,
          name: ed.edition_name,
          department_id: ed.department_id,
          department_code: ed.department_code,
        });
      }
      if (ed.department_id != null && ed.department_code != null) {
        setDepartment({ id: ed.department_id, code: ed.department_code });
      }
    },
    [setDepartment, setEdition, setEditor]
  );

  return { onDepartmentChange, onEditionChange, onEditorChange };
}
